import { useCallback, useSyncExternalStore } from 'react';

// WHAT IS DRAWN ON A PART BESIDES THE PART — the measurement lines, the face words, the box and
// the axis gizmo — kept in ONE place for every viewer showing it.
//
// A part is looked at in two viewers at once: the panel's, and the modal that opens out of
// it. They are two mountings of the same component, so each held its own copy of these four
// switches — and turning the measurements off in one and then opening the other brought them
// straight back. The angle had the same problem and was fixed years earlier by meshView, for
// the same reason and with the same argument: how you want to look at a part is a fact about
// you, not about which of two viewers happens to be open.
//
// NOT PERSISTED, and deliberately unlike meshView. An angle is a preference and survives the
// page; these are a working state — "let me see the measurements for a moment".
//
// OFF TO START WITH: a part opens as the mesh alone, and the marks button (MarksButton) brings
// the box, its numbers, the face words and the gizmo up together.
//
// The four are ids and not booleans because the viewer they came from draws several parts:
// `['part']` is this viewer's one instance switched off. Kept in that shape so nothing about
// the assembly phase's own use of them has to change.

export type PartMarks = {
  rulersOff: string[];
  facesOff: string[];
  // Whose BOX is off — the edges themselves, on their own switch where the viewer has one.
  boxesOff: string[];
  gizmoOn: boolean;
  // WHETHER A MODEL OF SEVERAL MESHES STANDS SPREAD APART — the Exploded view. Shared for the same
  // reason as the rest: the panel and the modal are one look at one model.
  exploded: boolean;
};

let marks: PartMarks = {
  rulersOff: ['part'], facesOff: ['part'], boxesOff: ['part'], gizmoOn: false,
  exploded: false,
};
const listeners = new Set<() => void>();

export const currentPartMarks = (): PartMarks => marks;

// A WHOLE VALUE AND NOT A FIELD AT A TIME. The one control that matters here sets all four
// together — the ruler button is "show me the marks" and not four separate questions — and a
// setter per field would let a caller write three of them and be interrupted before the
// fourth, which on screen is a gizmo standing over a part with no measurements on it.
export const setPartMarks = (next: Partial<PartMarks>): void => {
  marks = { ...marks, ...next };
  listeners.forEach((l) => l());
};

export const usePartMarks = (): PartMarks => {
  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);
  // Replaced rather than mutated, so the identity is a safe snapshot: React re-reads only
  // when something actually changed.
  return useSyncExternalStore(subscribe, () => marks);
};
